/**
 *   This class provides the base logic to display and mutate an employers calendar using AppointmentSlots,
 *   which itself is a wrapper for react-big-calendar.
 *
 *   Components that extend it receive all the state/methods required to pass the calendar data down
 *   to AppointmentSlots, receive/process user changes made there, and run the update mutation.
 * */

import { Component } from "react";
import moment from "moment-timezone";
import { Icon, Button, Image } from "semantic-ui-react";

import GoogleCalendarSetupModal from "common/components/Calendar/modals/GoogleCalendarSetupModal";
import {
  appSlot,
  appSlotStartMap,
  appSlotUpdate,
  findAvailabilityBlockLengthsHours,
  getTimezoneForCalendar,
  requestIsOutstanding,
  requestNeedsMoreAvailability,
  getBookableText,
  APPOINTMENT_SLOT_INTERVAL_IN_MINUTES,
  CREATE_SLOT,
  REMOVE_SLOT,
} from "common/helpers/calendar";
import { Icon as HAIcon, Toasts } from "hireart-components";
import { availabilityBufferModalToggle } from "../../../Managers/Hiring/actions/uiCandidate";
import { TimezoneAutocomplete } from "../TimezoneAutocomplete";

const DEFAULT_INTERVIEW_DURATION_SECONDS = 1800;

class CalendarBase extends Component {
  constructor(props) {
    super(props);

    this.state = {
      appSlotUpdates: new Map(), // holds create/deletes not yet persisted
      existingAppSlots: [], // slots in the db
      mergedAppSlots: [], // the result of applying the former to the latter
      openBufferModal: false,
      bufferType: null,
      alertMessage: null,
      timeZoneValue: getTimezoneForCalendar(),
      showGoogleDisconnectModal: false,
      refreshCalendar: false,
      refreshingCalendar: false,
    };
  }

  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const syncStatus = urlParams.get("sync-status");
    const syncDisconnect = urlParams.get("sync-disconnect");
    const syncRefresh = urlParams.get("sync-refresh");

    if (syncStatus === "success") {
      Toasts.success("Google Calendar successfully synced");
    }

    if (syncStatus === "fail") {
      Toasts.error("Google Calendar sync failed, please try again");
    }

    if (syncDisconnect === "success") {
      Toasts.success("Google Calendar successfully disconnected");
    }

    if (syncDisconnect === "fail") {
      Toasts.error("Google Calendar disconnection failed");
    }

    if (syncRefresh === "success") {
      Toasts.success("Google Calendar successfully refreshed");
    }

    if (syncRefresh === "fail") {
      Toasts.error("Google Calendar refresh failed");
    }

    this.timeoutID = this.timer();
  }

  timer = () =>
    setTimeout(() => {
      this.setState({ refreshCalendar: true });
    }, 300000);

  componentWillUnmount() {
    clearTimeout(this.timeoutID);
  }

  refreshCalendar = (e) => {
    e.stopPropagation();
    this.setState({ refreshingCalendar: true });
    document.location.href = "/managers/calendar/refresh";
  };

  showDisconnectModal = () => {
    if (this.state.refreshingCalendar === false) {
      this.setState({ showGoogleDisconnectModal: true });
    }
  };

  connectWithGoogleCTA = () => (
    <GoogleCalendarSetupModal
      trigger={
        <button type="button" className="google-sync-button">
          <Image
            className="google-image"
            inline
            src="/assets/google-icon.svg"
          />
          Sync Google Calendar
        </button>
      }
    />
  );

  actionIcon = () => (
    <span className="refresh-action">
      <HAIcon
        style={{ marginLeft: 5, marginRight: 5 }}
        size="sm"
        type="refresh"
        className={`brand-400-fg ${this.state.refreshingCalendar && "spin"}`}
      />
      <a onClick={(e) => this.refreshCalendar(e)}>
        {this.state.refreshingCalendar === false ? "Refresh" : "Refreshing..."}
      </a>
    </span>
  );

  syncedWithGoogleCTA = (calendar) => (
    <button
      type="button"
      className="google-sync-button"
      onClick={() => this.showDisconnectModal()}
      disabled={this.state.refreshingCalendar}
    >
      <Image className="google-image" inline src="/assets/google-icon.svg" />
      Synced {calendar.sync_profile_email}{" "}
      {this.state.refreshCalendar === false ? (
        <HAIcon
          style={{ marginLeft: 5 }}
          size="sm"
          type="check_circle"
          className="brand-400-fg"
        />
      ) : (
        this.actionIcon()
      )}
    </button>
  );

  ctaComponent = (calendar) => {
    if (calendar) {
      return calendar.google_synced
        ? this.syncedWithGoogleCTA(calendar)
        : this.connectWithGoogleCTA();
    }

    return null;
  };

  get appSlotIntervalMinutes() {
    return APPOINTMENT_SLOT_INTERVAL_IN_MINUTES;
  }

  get existingAppSlots() {
    return [...this.state.existingAppSlots];
  }

  get existingInterviewRequests() {
    if (!this.state.calendar) {
      return [];
    }

    return [...this.state.calendar.interview_requests];
  }

  get timeZone() {
    return this.state.timeZoneValue;
  }

  get availabilityBuffer() {
    return (
      (this.state.calendar && this.state.calendar.availability_buffer) ||
      "next_workday"
    );
  }

  get availabilityBufferTime() {
    return (
      (this.state.calendar && this.state.calendar.availability_buffer_time) || 0
    );
  }

  get alertMessage() {
    return this.state.alertMessage || "Calendar updated";
  }

  timeZoneSelect = () => {
    return (
      <TimezoneAutocomplete
        timezoneValue={this.timeZone}
        onChange={(value) => {
          this.setState({
            timeZoneValue: value,
            calendar: { ...this.state.calendar, time_zone_name: value },
          });
        }}
      />
    );
  };

  handleBufferModalClose = () => this.setState({ openBufferModal: false });

  handleBufferClick = () => {
    this.submitCalendar();
    this.props.dispatch(availabilityBufferModalToggle(true));
  };

  bufferTimeSelect = () => {
    return (
      <Button
        primary
        basic
        size="small"
        onClick={this.handleBufferClick}
        className="calendar-toolbar-button"
      >
        Bookable: {getBookableText(this.availabilityBuffer)}
        <Icon className="is-left-padded-small" fitted name="info circle" />
      </Button>
    );
  };

  //------------------------------------------------------------------
  //
  //                        DATA FLOW
  //
  //------------------------------------------------------------------

  componentWillReceiveProps(nextProps) {
    if (nextProps.data.calendar) {
      this.setState(
        {
          appSlotUpdates: new Map(),
          calendar: nextProps.data.calendar,
          timeZoneValue: getTimezoneForCalendar(nextProps.data.calendar),
          existingAppSlots: [],
          mergedAppSlots: [],
        },
        this.linkInterviewsToAppSlots
      );
    }
  }

  // link slots to corresponding interview requests, add title for react big calendar
  linkInterviewsToAppSlots() {
    const SLOT_LENGTH_SECONDS = this.appSlotIntervalMinutes * 60;

    const linkedSlots = appSlotStartMap(
      this.state.calendar.appointment_slots.map((slot) => ({
        ...slot,
        taken: false,
        title: "Available",
      }))
    );

    this.existingInterviewRequests.forEach(
      ({ duration, id, interview_type, interviewee, start_time }) => {
        // ignore unscheduled interview requests
        if (!start_time) {
          return;
        }

        for (
          let currStart = start_time;
          currStart < start_time + duration;
          currStart += SLOT_LENGTH_SECONDS
        ) {
          const slot = linkedSlots.get(currStart);

          // This can happen if the calendar has not synced with Google yet
          if (!slot) continue;

          Object.assign(slot, {
            interview_id: id,
            taken: true,
            title: `${interviewee} (${interview_type})`,
          });

          linkedSlots.set(currStart, slot);
        }
      }
    );

    this.setState(
      { existingAppSlots: [...linkedSlots.values()] },
      this.mergeAppSlotUpdates
    );
  }

  // apply local changes on top of database data
  mergeAppSlotUpdates() {
    const merged = appSlotStartMap(this.existingAppSlots);

    this.state.appSlotUpdates.forEach((type, start_time) => {
      if (type === CREATE_SLOT && !merged.has(start_time)) {
        merged.set(start_time, appSlot(start_time));
      } else {
        merged.delete(start_time);
      }
    });

    this.setState({
      mergedAppSlots: [...merged.values()],
    });
  }

  // integrate new creates/deletes
  updateAppSlots = (updates) => {
    const existingAppSlots = appSlotStartMap(this.existingAppSlots);
    const nextUpdates = new Map(this.state.appSlotUpdates);

    updates.forEach(({ appSlot: { start_time }, type }) => {
      if (existingAppSlots.has(start_time)) {
        if (type === CREATE_SLOT) {
          nextUpdates.delete(start_time);
        } else if (
          type === REMOVE_SLOT &&
          !existingAppSlots.get(start_time).taken
        ) {
          nextUpdates.set(start_time, REMOVE_SLOT);
        }
      } else if (type === CREATE_SLOT) {
        nextUpdates.set(start_time, CREATE_SLOT);
      } else {
        nextUpdates.delete(start_time);
      }
    });

    this.setState({ appSlotUpdates: nextUpdates }, this.mergeAppSlotUpdates);
  };

  createAppSlots = (appSlots) =>
    this.updateAppSlots(
      appSlots.map((slot) => ({ type: CREATE_SLOT, appSlot: slot }))
    );

  removeAppSlots = (appSlots) =>
    this.updateAppSlots(
      appSlots.map((slot) => ({ type: REMOVE_SLOT, appSlot: slot }))
    );

  //------------------------------------------------------------------
  //
  //                    VALIDATIONS & USER FEEDBACK
  //
  //------------------------------------------------------------------

  // In the interview request creation workflow we want to make the sure the interview
  // being created is included in validations, we pass those down as additionalInterviews

  // In the interview rescheduling workflow we often don't want to count the request
  // being rescheduled in the validations, so we pass its id down as requestToIgnore

  // true when the calendar's appointment slots have diverged from the database
  didUpdateAppSlots() {
    if (!this.state.calendar || this.state.appSlotUpdates.size === 0) {
      return false;
    }

    if (this.existingAppSlots.length !== this.state.mergedAppSlots.length) {
      return true;
    }

    const mergedStartTimes = new Set(
      this.state.mergedAppSlots.map((s) => s.start_time)
    );

    for (const { start_time } of this.existingAppSlots) {
      if (!mergedStartTimes.has(start_time)) {
        return true;
      }
    }

    return false;
  }

  availableAppSlotHours = () => {
    const now = moment().add(this.availabilityBufferTime, "seconds").unix();

    return this.state.mergedAppSlots
      .filter((slot) => !slot.taken && slot.start_time > now)
      .reduce(
        (availableHours, slot) =>
          availableHours + (slot.end_time - slot.start_time) / 60 / 60,
        0
      );
  };

  outstandingRequestHours(additionalInterviews = [], requestToIgnore) {
    return this.existingInterviewRequests
      .filter((ir) => requestIsOutstanding(ir, requestToIgnore))
      .concat(additionalInterviews)
      .reduce(
        (requestedHours, request) =>
          requestedHours + (request.duration || 0) / 60 / 60,
        0
      );
  }

  needMoreAvailabilityHours() {
    return this.existingInterviewRequests
      .filter((ir) => requestNeedsMoreAvailability(ir))
      .reduce((acc, request) => acc + request.duration / 60 / 60, 0);
  }

  openSlots() {
    const now = moment().unix();

    return this.state.mergedAppSlots
      .filter((slot) => !slot.taken && slot.start_time > now)
      .map((slot) => ({ ...slot }));
  }

  // Find outstanding requests that are too long to fit into the calendar, return the longest
  findUnscheduableInterviews = (additionalInterviews = [], requestToIgnore) => {
    const availableBlockLengths = findAvailabilityBlockLengthsHours(
      this.openSlots(),
      this.timeZone
    );

    const interviewsDescending = [...this.existingInterviewRequests]
      .filter((ir) => requestIsOutstanding(ir, requestToIgnore))
      .concat(additionalInterviews)
      .sort((a, b) => b.duration - a.duration);

    for (const interview of interviewsDescending) {
      const scheduable = availableBlockLengths.some(
        (lengthHours) => lengthHours >= interview.duration / 60 / 60
      );

      if (!scheduable) {
        return interview;
      }
    }

    return null;
  };

  hasError(additionalInterviews = [], requestToIgnore) {
    const unscheduableInterview = this.findUnscheduableInterviews(
      additionalInterviews,
      requestToIgnore
    );

    if (unscheduableInterview) {
      return true;
    }

    return (
      this.outstandingRequestHours(additionalInterviews, requestToIgnore) >
      this.availableAppSlotHours()
    );
  }

  availabilityDetails(additionalInterviews = [], requestToIgnore) {
    const availableHours = this.availableAppSlotHours();
    const requestedHours = this.outstandingRequestHours(
      additionalInterviews,
      requestToIgnore
    );

    if (requestedHours == 0) return;

    return (
      <>
        <div className="availability-details">
          {availableHours === 0 &&
            "You have 0 hours available after your buffer time."}
          {availableHours > 0 && (
            <div>
              You listed
              <strong>
                {` ${availableHours} hour${
                  availableHours <= 1 ? "" : "s"
                } available for interviews.`}
              </strong>
            </div>
          )}
        </div>
        <div className="availability-details">
          You have
          <strong>
            {` ${requestedHours} hour${
              requestedHours <= 1 ? "" : "s"
            } of interview requests outstanding.`}
          </strong>
        </div>
      </>
    );
  }

  userFeedback(additionalInterviews = [], requestToIgnore) {
    const unscheduableInterview = this.findUnscheduableInterviews(
      additionalInterviews,
      requestToIgnore
    );

    let message =
      "You can add more availability to make it even easier for candidates to schedule interviews.";
    let className = "error";
    let iconName = "error";

    if (
      this.outstandingRequestHours(additionalInterviews, requestToIgnore) >
      this.availableAppSlotHours()
    ) {
      message =
        "Add more availability outside your buffer time so candidates can schedule interviews. Availability must equal or exceed hours of interview requests outstanding.";
    } else if (unscheduableInterview) {
      const interviewDurationHours =
        (unscheduableInterview.duration || DEFAULT_INTERVIEW_DURATION_SECONDS) /
        60 /
        60;
      message = `Please add a block of availability of at least ${interviewDurationHours} hour${
        interviewDurationHours === 1 ? "" : "s"
      } so you can schedule your interview with ${
        unscheduableInterview.interviewee
      }`;
    } else {
      className = "success";
      iconName = "check_circle";
    }

    return (
      <div id="feedback-message" className="flex items-start gap-8">
        <HAIcon className={className} type={iconName} />
        <p className={className}>{message}</p>
      </div>
    );
  }

  //------------------------------------------------------------------
  //
  //                          UPDATE MUTATION
  //
  //------------------------------------------------------------------

  // override this for post-submit action
  afterSubmit() {
    return null;
  }

  submitCalendar() {
    const existingAppSlotMap = appSlotStartMap(this.existingAppSlots);

    const updates = [];

    this.state.appSlotUpdates.forEach((type, start_time) => {
      const startTimeTaken = existingAppSlotMap.has(start_time);
      const update = appSlotUpdate(start_time, type, existingAppSlotMap);

      if (type === CREATE_SLOT ? !startTimeTaken : startTimeTaken) {
        updates.push(update);
      }
    });

    this.setState({ submitting: true }, () => {
      this.props
        .mutate({
          variables: {
            time_zone_name: this.timeZone,
            availability_buffer: this.availabilityBuffer,
            appointment_slots: updates.length ? updates : {},
          },
        })
        .then(({ data }) => {
          this.setState(
            {
              appSlotUpdates: new Map(),
              calendar: {
                ...this.props.data.calendar,
                appointment_slots: data.updateCalendar.appointment_slots,
                time_zone_name: data.updateCalendar.time_zone_name,
                availability_buffer: data.updateCalendar.availability_buffer,
                availability_buffer_time:
                  data.updateCalendar.availability_buffer_time,
              },
              existingAppSlots: data.updateCalendar.appointment_slots,
              mergedAppSlots: [],
            },
            () => {
              this.afterSubmit(this.alertMessage);
              this.linkInterviewsToAppSlots();
              this.handleBufferModalClose();
              this.setState({ alertMessage: null });
            }
          );
        })
        .catch((error) => {
          console.log(error);
          window.alert("there was an error sending the query", error);
        })
        .finally(() => {
          this.setState({ submitting: false });
        });
    });
  }
}

export default CalendarBase;
